import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, FileText, UserCheck, CalendarDays,
  LogOut, Menu, X, ChevronRight, Bell, FolderCheck, ShieldCheck, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils";

const BASE_NAV = [
  { label: "Dashboard",      href: "/admin",               icon: LayoutDashboard },
  { label: "Users",          href: "/admin/users",          icon: Users },
  { label: "Consultations",  href: "/admin/consultations",  icon: FileText },
  { label: "Advisors",       href: "/admin/advisors",       icon: UserCheck },
  { label: "Appointments",   href: "/admin/appointments",   icon: CalendarDays },
  { label: "KYC Documents",  href: "/admin/kyc",            icon: FolderCheck },
  { label: "Footfall",       href: "/admin/footfall",       icon: Eye },
];

const SUPER_ADMIN_NAV = [
  { label: "Admin Management", href: "/admin/management", icon: ShieldCheck },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isSuperAdmin = user?.role === "super_admin";
  const NAV = isSuperAdmin ? [...BASE_NAV, ...SUPER_ADMIN_NAV] : BASE_NAV;

  return (
    <div className="min-h-screen bg-[hsl(var(--zt-beige))] flex">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-[hsl(var(--zt-teal))] flex flex-col transition-transform duration-300 shadow-[4px_0_30px_rgba(0,0,0,0.15)]",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo area */}
        <div className="h-20 flex items-center px-6 border-b border-white/10 flex-shrink-0">
          <button onClick={() => navigate("/")} className="flex items-center gap-3 group">
            <img src="/zenstrust-logo.png" alt="Zenstrust" className="h-10 w-auto rounded-md object-contain" />
            <div>
              <span className="block font-heading uppercase tracking-[0.2em] text-[9px] text-[hsl(var(--zt-gold))] font-bold">Admin</span>
              <span className="block font-heading uppercase tracking-[0.15em] text-[10px] text-white/80 font-semibold">Portal</span>
            </div>
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden text-white/60 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {NAV.map(({ label, href, icon: Icon }) => {
            const active = location.pathname === href || (href !== "/admin" && location.pathname.startsWith(href));
            return (
              <Link
                key={href}
                to={href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl font-heading uppercase tracking-[0.15em] text-[11px] font-semibold transition-all duration-200",
                  active
                    ? "bg-white/15 text-white shadow-sm"
                    : "text-white/65 hover:bg-white/10 hover:text-white"
                )}
              >
                <Icon size={16} className={active ? "text-[hsl(var(--zt-gold))]" : "text-white/50"} />
                {label}
                {active && <ChevronRight size={13} className="ml-auto text-[hsl(var(--zt-gold))]/70" />}
              </Link>
            );
          })}
        </nav>

        {/* User info */}
        <div className="px-4 py-4 border-t border-white/10 flex-shrink-0">
          <div className="bg-white/5 rounded-xl px-4 py-3 space-y-2">
            <p className="text-[10px] font-heading uppercase tracking-widest text-white/50 font-semibold">
              {isSuperAdmin ? "Super Admin" : "Admin"}
            </p>
            <p className="text-sm text-white font-semibold font-body truncate">{user?.name || user?.email}</p>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[10px] font-heading uppercase tracking-wider text-white/50 hover:text-[hsl(var(--zt-gold))] transition-colors pt-1"
            >
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-[hsl(var(--zt-beige-deep))] flex items-center px-6 gap-4 sticky top-0 z-30 shadow-sm">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-[hsl(var(--zt-teal))] hover:text-[hsl(var(--zt-gold))] transition-colors"
          >
            <Menu size={20} />
          </button>
          <div className="flex-1" />
          <button className="w-9 h-9 rounded-full bg-[hsl(var(--zt-beige))] grid place-items-center text-[hsl(var(--zt-teal))]/60 hover:text-[hsl(var(--zt-teal))] transition-colors relative">
            <Bell size={16} />
          </button>
          <div className="flex items-center gap-2 pl-3 border-l border-[hsl(var(--zt-beige-deep))]">
            <div className="w-8 h-8 rounded-full bg-[hsl(var(--zt-teal))] grid place-items-center text-white text-xs font-bold">
              {(user?.name || user?.email || "A").charAt(0).toUpperCase()}
            </div>
            <span className="text-xs font-heading font-semibold text-[hsl(var(--zt-teal))] hidden sm:block">
              {user?.name?.split(" ")[0] || "Admin"}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
